import React, { useState, useEffect } from 'react'
import './index.css'
import {
  Layout, Typography, Card, Alert, Table, Radio,
  Tooltip, Space, Select, Button, message, Tag
} from 'antd'
import { BarsOutlined, LinkOutlined, DownloadOutlined } from '@ant-design/icons'
import DemoQueryBuilder from '../../components/QueryBuilder'
import useBoundStore from '../../store'
import { useSearch } from '../../hooks/useSearch'
import ExportJsonExcel from 'js-export-excel'
import axios from 'axios'
import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts'
import worldGeoJson from '../../assets/world.json'

echarts.registerMap('world', worldGeoJson)

const { Content } = Layout
const { Title } = Typography

const Home = () => {
  // const { queryData, setQueryData} = useBoundStore()
  const { queryData, setQueryData, loadingData, setLoadingData } = useBoundStore()
  const { getColumnSearchProps } = useSearch()

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
  })

  // const [loadingData, setLoadingData] = useState(false)

  useEffect(() => {
    
    const loadDefaultData = async () => {
      try {
        setLoadingData(true)
        const { data } = await axios.post('/api/query', {
          query: "Run IS NOT NULL"
        })
        setQueryData(data)
      } catch (err) {
        message.error('Failed to load data')
      } finally {
        setLoadingData(false) // ✅ 加载结束
      }
    }
    loadDefaultData()
  }, [setQueryData,setLoadingData])

  const columns = [
    {
      title: 'Run',
      dataIndex: 'Run',
      key: 'Run',
      fixed: 'left',
      width: 120,
      render: (value) => (
        <Tooltip placement="topLeft" title={value}>
          <a href={`/sample/${value}`} style={{ color: '#1677ff', fontWeight: 600 }}>{value}</a>
        </Tooltip>
      ),
      sorter: (a, b) => a.Run.localeCompare(b.Run, undefined, { numeric: true }),
    },
    {
      title: 'Assay Type',
      dataIndex: 'Assay_Type',
      key: 'Assay_Type',
      width: 110,
      ellipsis: true,
    },
    {
      title: 'BioProject',
      dataIndex: 'BioProject',
      key: 'BioProject',
      width: 150,
      render: (value) => (
        <Tooltip title={value}>
          <a href={`https://www.ncbi.nlm.nih.gov/bioproject/?term=${value}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1677ff' }}>
            <Space><LinkOutlined />{value}</Space>
          </a>
        </Tooltip>
      ),
    },
    {
      title: 'Curated',
      dataIndex: 'curated_projects',
      key: 'curated_projects',
      width: 120,
      render: (value) => <Tag color={value ? 'success' : 'error'}>{value ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Whether Correct',
      dataIndex: 'Whether_correct',
      key: 'Whether_correct',
      width: 150,
      render: (value) => <Tag color={value ? 'success' : 'error'}>{value ? 'Yes' : 'No'}</Tag>,
    },
    { title: 'Paper Name', dataIndex: 'Paper_name', key: 'Paper_name', width: 160, ellipsis: true },
    { title: 'Description', dataIndex: 'Project_description', key: 'Project_description', width: 160, ellipsis: true },
    { title: 'BioSample', dataIndex: 'BioSample', key: 'BioSample', width: 120, ellipsis: true },
    { title: 'Experiment', dataIndex: 'Experiment', key: 'Experiment', width: 120, ellipsis: true },
    { title: 'Habitat', dataIndex: 'Organism', key: 'Organism', width: 130, ellipsis: true },
    { title: 'Release Date', dataIndex: 'ReleaseDate', key: 'ReleaseDate', width: 130, ...getColumnSearchProps('ReleaseDate') },
    { title: 'Create Date', dataIndex: 'create_date', key: 'create_date', width: 130, ...getColumnSearchProps('create_date') },
    { title: 'Country', dataIndex: 'geo_loc_name_country', key: 'geo_loc_name_country', width: 110 },
    { title: 'Continent', dataIndex: 'geo_loc_name_country_continent', key: 'geo_loc_name_country_continent', width: 110 },
    { title: 'Collection Date', dataIndex: 'Collection_Date', key: 'Collection_Date', width: 130, ...getColumnSearchProps('Collection_Date') },
    { title: 'Lat Lon', dataIndex: 'lat_lon', key: 'lat_lon', width: 120 },
    { title: 'Host', dataIndex: 'HOST', key: 'HOST', width: 110 },
    { title: 'Sequencing Method', dataIndex: 'Sequencing_method', key: 'Sequencing_method', width: 150 },
  ]

  const [filteredColumns, setFilteredColumns] = useState(columns)
  const options = columns.map(col => ({ label: col.title, value: col.title }))

  const handleChange = (selectedHiddenTitles) => {
    setFilteredColumns(columns.filter(col => !selectedHiddenTitles.includes(col.title)))
  }

  const downloadExcel = () => {
    if (!queryData.length) { message.warning('No data to export!'); return }
    const dataTable = queryData.map(item => {
      const newItem = { ...item }
      Object.keys(newItem).forEach(key => { if (typeof newItem[key] === 'boolean') newItem[key] = newItem[key].toString() })
      return newItem
    })
    const option = {
      fileName: 'LRMrepo_Data',
      datas: [{ sheetData: dataTable, sheetName: 'Data', sheetFilter: filteredColumns.map(c => c.key), sheetHeader: filteredColumns.map(c => c.title) }]
    }
    new ExportJsonExcel(option).saveExcel()
    message.success('Export successfully')
  }

  const [counts, setCounts] = useState({ run: null, project: null, organism: null })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [mapOption, setMapOption] = useState({})
  const [totalSamples, setTotalSamples] = useState(0)
  const [topCountries, setTopCountries] = useState([])

  useEffect(() => {
    const fetchCount = async () => {
      try {
        setLoading(true)
        const { data } = await axios.get('/api/getCount')
        if (Array.isArray(data.quantity) && data.quantity.length >= 3) {
          setCounts({ run: data.quantity[0], project: data.quantity[1], organism: data.quantity[2] })
        }
      } catch (err) { setError('Load failed') } finally { setLoading(false) }
    }
    fetchCount()

    // 加载世界地图
    const loadMap = async () => {
      try {
        const locRes = await axios.get('/api/sampleLocations')
        const norm = (c) => {
          const m = { USA:'United States', French:'France', UK:'United Kingdom', Noreg:'Norway',
            'French Guiana':'France', Korea:'South Korea', 'The Kingdom of Denmark':'Denmark',
            'Hong Kong':'China', Taiwan:'China' }
          return m[c] || c
        }
        const cc = {}
        locRes.data.forEach((d) => {
          if (!d.country || d.country === 'unknown' || d.country === 'uncalculated') return
          const cn = norm(d.country)
          cc[cn] = (cc[cn] || 0) + 1
        })
        setTotalSamples(locRes.data.length)
        const maxCount = Math.max(...Object.values(cc), 1)
        setTopCountries(Object.entries(cc).sort((a, b) => b[1] - a[1]).slice(0, 15))
        setMapOption({
          tooltip: { formatter: (p) => '<b>' + p.name + '</b><br/>Samples: <b style="color:#1677ff">' + (cc[p.name] || 0) + '</b>' },
          visualMap: { type: 'piecewise', pieces: [
            { min: 100, color: '#006d2c' },
            { min: 50, max: 99, color: '#31a354' },
            { min: 10, max: 49, color: '#74c476' },
            { min: 1, max: 9, color: '#a1d99b' },
            { min: 0, max: 0, color: '#f0f0f0' },
          ], left: 12, top: 'center' },
          geo: { map: 'world', roam: true, zoom: 1.2,
            itemStyle: { areaColor: '#e5f5e0', borderColor: '#ccc' },
            emphasis: { itemStyle: { areaColor: '#74c476' } },
            regions: Object.keys(cc).map((c) => ({
              name: c, itemStyle: { areaColor: '' }, // visualMap 自动配色
            })),
          },
          series: [{
            type: 'map', map: 'world', geoIndex: 0,
            data: Object.entries(cc).map(([k, v]) => ({ name: k === 'Hong Kong' ? 'China' : k, value: v })),
          }],
        })
      } catch (e) { console.error('Map load failed', e) }
    }
    loadMap()
  }, [])

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ padding: '24px 16px', maxWidth: 1200, margin: '0 auto' }}>

        {/* 顶部大标题区 */}
        <Card style={{ marginBottom: 20, borderRadius: 12 }} bodyStyle={{ padding: '16px 20px' }}>
          <Title level={2} style={{ margin: 0, color: '#002156', fontSize: 22 }}>
            LRMrepo
            <span style={{ fontSize: 15, color: '#666', marginLeft: 12 }}>Long-Read Metagenome Repository</span>
          </Title>
        </Card>

        {/* 第一行：简介 + 统计 */}
        <div style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          <Card 
            style={{ 
              flex: 2, 
              borderRadius: 12, 
              minWidth: 280, 
              overflow: 'hidden' 
            }} 
            bodyStyle={{ padding: 0 }}
          >
            <img 
              src="intro.png" 
              alt="LRMrepo introduction"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover', // 保证图片不变形，覆盖卡片
                display: 'block'
              }}
            />
          </Card>
          {/* 左侧简介 */}
          {/* <Card 
            style={{ flex: 2, borderRadius: 12, minWidth: 280 }} 
            title="About LRMrepo" 
            bodyStyle={{ padding: '16px 20px' }}
          >
            <p style={{ fontSize: 14, lineHeight: 1.6 }}>
              LRMrepo is a curated database of consistently annotated long-read metagenomes,
              providing high-quality metadata and organized samples for research.
            </p>
            <ul style={{ paddingLeft: 20, marginTop: 8, fontSize:14 }}>
              <li>Manual curation for long-read metagenomic samples</li>
              <li>Samples grouped by phenotype (human, animal, health & disease)</li>
              <li>Interactive tools for fast data browsing</li>
            </ul>
          </Card> */}

          {/* 右侧统计 */}
          <Card style={{ flex: 1, borderRadius: 12, minWidth: 240}} title="Database Statistics" bodyStyle={{ padding: '16px 20px' }}>
            <div style={{fontSize:14}}>
              <div style={{ marginBottom: 10 }}>
                <strong>Runs/Samples:</strong> 
                {' '}
                {loading ? 'Loading...' : error ? error : <strong style={{ color: '#1677ff' }}>{counts.run}</strong>}
              </div>
              <div style={{ marginBottom: 10 }}>
                <strong>Projects:</strong> 
                {' '}
                {loading ? 'Loading...' : error ? error : <strong style={{ color: '#1677ff' }}>{counts.project}</strong>}
              </div>
              <div>
                <strong>Habitats:</strong> 
                {' '}
                {loading ? 'Loading...' : error ? error : <strong style={{ color: '#1677ff' }}>{counts.organism}</strong>}
              </div>
            </div>
          </Card>
        </div>

        {/* 世界地图 */}
        <Card style={{ marginBottom: 20, borderRadius: 12 }} bodyStyle={{ padding: '12px' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', borderLeft: '3px solid #1677ff', paddingLeft: 10, marginBottom: 8 }}>
            Global Distribution of Sampling Locations ({totalSamples} samples)
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 3, minWidth: 480 }}>
              <ReactECharts option={mapOption} style={{ height: 480 }} />
            </div>
            <div style={{ flex: 1, minWidth: 160, fontSize: 13, maxHeight: 480, overflowY: 'auto' }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: '#1f2937', fontSize: 13, textAlign: 'center' }}>Top Sampling Countries</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #a1d99b' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #a1d99b' }}>
                    <th style={{ padding: '4px 6px', fontSize: 12, color: '#1f2937', borderRight: '1px solid #a1d99b' }}>SN</th>
                    <th style={{ padding: '4px 6px', fontSize: 12, color: '#1f2937', borderRight: '1px solid #a1d99b' }}>Country</th>
                    <th style={{ padding: '4px 6px', fontSize: 12, color: '#1f2937' }}>Samples</th>
                  </tr>
                </thead>
                <tbody>
                  {topCountries.map(([c, n], i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #c6e8c0' }}>
                      <td style={{ padding: '3px 6px', color: '#999', borderRight: '1px solid #a1d99b', textAlign: 'center' }}>{i + 1}</td>
                      <td style={{ padding: '3px 6px', color: '#555', borderRight: '1px solid #a1d99b', textAlign: 'center' }}>{c}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'center', fontWeight: 600, color: '#1677ff' }}>{n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        {/* 第二行：搜索框 单独一行 */}
        <div style={{ marginBottom: 20 }}>
          <Card
            style={{ borderRadius: 12 }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{
              marginBottom: 16,
              fontSize: 16,
              fontWeight: 600,
              color: '#1f2937',
              borderLeft: '3px solid #1677ff',
              paddingLeft: 10
            }}>
              Advanced Query Search
            </div>

            <Radio.Group
              defaultValue="a"
              buttonStyle="solid"
              style={{ width: '100%', display: 'flex', marginBottom: 16 }}
            >
              <Radio.Button value="a" style={{ flex: 1, textAlign: 'center' }}>
                <BarsOutlined /> Samples
              </Radio.Button>
              {/* <Radio.Button value="b" style={{ flex: 1, textAlign: 'center' }}>
                <BarsOutlined /> Projects
              </Radio.Button> */}
            </Radio.Group>

            <div style={{
              background: '#f7f9fc',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              overflow: 'hidden'
            }}>
              <DemoQueryBuilder />
            </div>
          </Card>
        </div>

        {/* 表格工具栏 */}
        <Card style={{ marginBottom: 20, borderRadius: 12 }} bodyStyle={{ padding: '16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <span style={{ fontWeight: 500, minWidth: 100 }}>Hide Columns:</span>
              <Select mode="multiple" allowClear placeholder="Select columns" style={{ minWidth: 240, flex: 1 }} onChange={handleChange} options={options} />
            </div>
            <div>Total: <strong style={{ color: '#1677ff' }}>{queryData.length}</strong> entries</div>
          </div>
        </Card>

        {/* 表格区域 */}
        <Card style={{ marginBottom: 20, borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
          <Table
            loading={loadingData}
            dataSource={queryData}
            columns={filteredColumns}
            scroll={{ x: '100%' }}
            rowKey={(r, i) => i}
            // ✅ 修复：同时处理 onChange（页码）+ onShowSizeChange（每页条数）
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total) => `Total ${total} entries`,
              pageSizeOptions: ['10', '15', '30', '50', '100'],
              // 👇 新增：切换页码时更新 current
              onChange: (page, pageSize) => {
                setPagination({
                  current: page,
                  pageSize: pageSize,
                })
              },
              // 👇 切换每页条数时也更新
              onShowSizeChange: (current, size) => {
                setPagination({
                  current: 1,
                  pageSize: size,
                })
              },
            }}
            bordered={false}
          />
        </Card>

        {/* 底部导出 + 提示 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <Button type="primary" icon={<DownloadOutlined />} onClick={downloadExcel}>
            Export to Excel
          </Button>
          <Alert message="Tip" description="For detailed usage, please visit the Help page." type="info" showIcon />
        </div>

      </Content>
    </Layout>
  )
}

export default Home